import { ConciergeAvatar } from "@/components/brand/ConciergeAvatar";
import { ImageLightbox } from "@/components/chat/ImageLightbox";
import { cn } from "@/lib/utils";
import type { ChatRole } from "@/types";
import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface MessageBubbleProps {
  sender: ChatRole;
  content: string;
  /** Guide photos rendered below the reply text (assistant only). */
  imageUrls?: string[];
}

/**
 * A single chat bubble. Assistant replies render as GitHub-flavored markdown
 * (.chat-prose — tables, lists, links) beside the concierge avatar; user
 * messages render as plain pre-wrapped text inside the primary bubble.
 * Guide photos render as one bundled preview (first photo + "+N" badge) that
 * opens a fullscreen lightbox with prev/next navigation.
 */
export function MessageBubble({ sender, content, imageUrls }: MessageBubbleProps) {
  const isUser = sender === "user";
  const [lightboxAt, setLightboxAt] = useState<number | null>(null);
  const images = imageUrls ?? [];
  return (
    <div
      className={cn(
        "flex animate-rise items-end gap-2.5",
        isUser ? "justify-end" : "justify-start"
      )}
    >
      {!isUser && <ConciergeAvatar size={34} className="mb-0.5" />}
      <div
        className={cn(
          "max-w-[80%] break-words px-4 py-2.5 text-sm leading-relaxed",
          isUser
            ? "whitespace-pre-wrap rounded-[1.25rem] rounded-br-md bg-primary text-primary-foreground shadow-sm"
            : "rounded-[1.25rem] rounded-bl-md border border-border/60 bg-card text-foreground shadow-xs"
        )}
      >
        {isUser ? (
          content
        ) : (
          <>
            <div className="chat-prose">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
            </div>
            {images.length > 0 ? (
              <button
                type="button"
                aria-label={`안내 이미지 ${images.length}장 크게 보기`}
                onClick={() => setLightboxAt(0)}
                className="relative mt-2 block overflow-hidden rounded-xl"
              >
                <img
                  src={images[0]}
                  alt=""
                  loading="lazy"
                  className="h-36 w-full max-w-64 object-cover transition-transform hover:scale-[1.02]"
                />
                {images.length > 1 ? (
                  <span className="absolute bottom-2 right-2 rounded-full bg-black/60 px-2 py-0.5 text-xs font-medium text-white">
                    +{images.length - 1}
                  </span>
                ) : null}
              </button>
            ) : null}
            {lightboxAt !== null ? (
              <ImageLightbox
                urls={images}
                initialIndex={lightboxAt}
                onClose={() => setLightboxAt(null)}
              />
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
